// Staff-side API for Pages (list, editor, gallery, domains). One typed function per
// endpoint in nl_aos_replit_ready/nl_aos_clean/routes/pages.js; the page builder never
// calls fetch for its data anywhere else. The public page bundle must not import this
// file (it pulls in the staff session handling from ../api).
import { api, ApiError } from '../api';
import type { LandingPage, PageContent, PageEdits, PageTheme } from './model/pages';

export { ApiError };
export type { LandingPage, PageEdits };

const enc = encodeURIComponent;
const json = (method: string, body?: unknown): RequestInit => ({ method, body: body === undefined ? undefined : JSON.stringify(body) });

// ── Pages ────────────────────────────────────────────────────

/** Every page, newest change first. */
export const listPages = () => api<LandingPage[]>('/api/pages');

export const getPage = (id: string) => api<LandingPage>(`/api/pages/${enc(id)}`);

export interface CrmFormDraft {
  questions: unknown[];
  settings: Record<string, unknown>;
}

export interface NewPageInput {
  name: string;
  content?: PageContent;
  theme?: PageTheme;
  /** Link an existing CRM form. */
  form_id?: number | null;
  /** Or have the server create a draft CRM form for this page (templates). */
  form?: CrmFormDraft | null;
}

export const createPage = (input: NewPageInput) => api<LandingPage>('/api/pages', json('POST', input));

/** `base_updated_at` = the page's updated_at when the editor loaded or last wrote it: a newer row answers 409 (code 'stale'). */
export type PageWrite = Partial<PageEdits> & { base_updated_at?: string };

export const updatePage = (id: string, edits: PageWrite) => api<LandingPage>(`/api/pages/${enc(id)}`, json('PUT', edits));

/** Writes the edits and puts the page live. A linked draft form goes live with it (form_went_live). */
export const publishPage = (id: string, edits: PageWrite = {}) =>
  api<LandingPage & { form_went_live?: boolean }>(`/api/pages/${enc(id)}/publish`, json('POST', edits));

export const unpublishPage = (id: string) => api<LandingPage>(`/api/pages/${enc(id)}/unpublish`, json('POST', {}));

export const duplicatePage = (id: string) => api<LandingPage>(`/api/pages/${enc(id)}/duplicate`, json('POST', {}));

export const deletePage = (id: string) => api<{ ok: true }>(`/api/pages/${enc(id)}`, { method: 'DELETE' });

export type SlugProblem = 'invalid' | 'reserved' | 'taken';

export const slugAvailable = (slug: string, exceptId?: string) =>
  api<{ available: boolean; reason?: SlugProblem }>(`/api/pages/slug-available?slug=${enc(slug)}${exceptId ? `&except=${enc(exceptId)}` : ''}`);

/** The page's private preview token (minted once). Preview URL = /p/<slug>?pt=<token>. */
export const ensurePreviewToken = (id: string) => api<{ token: string }>(`/api/pages/${enc(id)}/preview-token`, json('POST', {}));

// ── History ──────────────────────────────────────────────────

export interface PageVersion {
  id: number;
  label: string;
  created_at: string;
  created_by_name: string | null;
  snapshot: PageEdits;
}

export const listVersions = (id: string) => api<PageVersion[]>(`/api/pages/${enc(id)}/versions`);

export const saveVersion = (id: string, label: string, snapshot: PageEdits) =>
  api<PageVersion>(`/api/pages/${enc(id)}/versions`, json('POST', { label, snapshot }));

// ── Analytics ────────────────────────────────────────────────

export interface PageTrackingSite {
  id: number; client_id: number; client_name: string; label: string | null;
  domains: string[]; active: boolean;
}
export const listPageTrackingSites = () => api<{ sites: PageTrackingSite[] }>('/api/pages/tracking-sites');

export interface PageStats {
  views: number; visitors: number; clicks: number; leads: number; conversion: number;
  /** Leads that came with a Google Ads click. */
  google_leads?: number;
  /** Leads who then booked a call (not cancelled). */
  booked?: number;
}
export interface PageDay { day: string; views: number; clicks: number; leads: number }
export interface PageSource { source: string; views: number }
export interface PageOverviewRow { page_id: string; views: number; visitors: number; clicks: number; leads: number }

export const pageStats = (id: string, days = 30) => api<PageStats>(`/api/pages/${enc(id)}/stats?days=${days}`);
export const pageDaily = (id: string, days = 14) => api<PageDay[]>(`/api/pages/${enc(id)}/daily?days=${days}`);
export const pageSources = (id: string, days = 30) => api<PageSource[]>(`/api/pages/${enc(id)}/sources?days=${days}`);
export const pagesOverview = (days = 30) => api<PageOverviewRow[]>(`/api/pages/overview?days=${days}`);

// ── AI tab ───────────────────────────────────────────────────

export interface AiEditResult {
  result: { updates: unknown[]; added: unknown[]; removed: unknown[] };
  summary: string;
  model?: string;
}

/** Sends only section ids, types and words (see model/page-ai.ts projectForAi). */
export const aiEditSections = (input: { instruction: string; page_name: string; sections: unknown[] }) =>
  api<AiEditResult>('/api/pages/ai-edit', json('POST', input));

// ── Saved templates ──────────────────────────────────────────

export interface SavedTemplate {
  id: string;
  name: string;
  description: string;
  content: PageContent;
  theme: PageTheme;
  /** The linked CRM form's questions and settings when the template was saved, or null. */
  form: CrmFormDraft | null;
  created_at: string;
}

export const listSavedTemplates = () => api<SavedTemplate[]>('/api/pages/templates');

export const saveTemplate = (input: { name: string; description?: string; content: PageContent; theme: PageTheme; form?: CrmFormDraft | null }) =>
  api<SavedTemplate>('/api/pages/templates', json('POST', input));

export const deleteTemplate = (id: string) => api<{ ok: true }>(`/api/pages/templates/${enc(id)}`, { method: 'DELETE' });

// ── Images ───────────────────────────────────────────────────

export const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/avif', 'image/svg+xml'];
export const MAX_IMAGE_BYTES = 4 * 1024 * 1024;

/**
 * Uploads one image for a page. Answers a RELATIVE url (/api/public/pages/img/<id>.<ext>)
 * that works on the app host and on every custom domain. Raw body, so it cannot go
 * through api() (that would label it JSON).
 */
export async function uploadPageImage(file: Blob, fileName = ''): Promise<{ id: string; url: string }> {
  if (!IMAGE_TYPES.includes(file.type)) throw new ApiError('Use a PNG, JPG, WebP, GIF, AVIF or SVG image.', 400);
  if (file.size > MAX_IMAGE_BYTES) throw new ApiError('That image is over 4 MB. Use a smaller one.', 413);
  let response: Response;
  try {
    response = await fetch('/api/pages/images', {
      method: 'POST',
      body: file,
      credentials: 'include',
      headers: { 'Content-Type': file.type, Accept: 'application/json', 'X-NL-AOS-Request': '1', ...(fileName ? { 'X-File-Name': encodeURIComponent(fileName.slice(0, 120)) } : {}) },
    });
  } catch {
    throw new ApiError('The upload did not reach the server. Check your connection and try again.', 0);
  }
  const payload = await response.json().catch(() => null) as { id?: string; url?: string; error?: string } | null;
  if (!response.ok || !payload?.url || !payload.id) throw new ApiError(payload?.error || `Upload failed (${response.status})`, response.status);
  return { id: payload.id, url: payload.url };
}

// ── Custom domains ───────────────────────────────────────────

export interface DnsRecord { type: 'A' | 'CNAME' | 'TXT'; name: string; value: string }

export interface PageDomain {
  id: number;
  domain: string;
  apex: string;
  status: 'pending' | 'active' | 'error';
  records: DnsRecord[];
  root_page_id: string | null;
  root_page: { id: string; name: string; slug: string; status: 'draft' | 'published' } | null;
  last_checked_at: string | null;
  last_error: string;
  created_at: string;
}

export interface DomainsResponse {
  /** False when the server has no Vercel credentials: list only, no add/check/remove. */
  configured: boolean;
  /** The app's own host, e.g. nl-aos-ten.vercel.app (pages live at https://<app_host>/p/<slug>). */
  app_host: string;
  domains: PageDomain[];
}

export const listDomains = () => api<DomainsResponse>('/api/pages/domains');
export const addDomain = (domain: string) => api<PageDomain>('/api/pages/domains', json('POST', { domain }));
export const checkDomain = (id: number) => api<PageDomain>(`/api/pages/domains/${id}/check`, json('POST', {}));
export const setDomainRoot = (id: number, rootPageId: string | null) => api<PageDomain>(`/api/pages/domains/${id}`, json('PUT', { root_page_id: rootPageId }));
export const removeDomain = (id: number) => api<{ ok: true }>(`/api/pages/domains/${id}`, { method: 'DELETE' });

// ── CRM pickers (forms and calendars live in the CRM) ────────

export interface CrmFormSummary {
  id: number;
  slug: string;
  name: string;
  status: 'draft' | 'live';
  submissions: number;
  updated_at: string;
  /** calendar_id set: the form ends with that calendar for qualified leads. */
  settings?: { calendar_id?: number | null };
}

export interface CrmCalendarSummary {
  id: number;
  slug: string;
  name: string;
  active: boolean;
  duration_min: number;
}

/** A CRM form with its questions and settings (Save as template keeps a copy of them). */
export interface CrmFormFull extends CrmFormSummary {
  questions: unknown[];
  settings: Record<string, unknown>;
}

/** Live and draft CRM forms (archived ones are left out by the server). */
export const listCrmForms = () => api<CrmFormSummary[]>('/api/crm/forms');

export const getCrmForm = (id: number) => api<CrmFormFull>(`/api/crm/forms/${id}`);

/** A new draft CRM form with the default questions; edit it in CRM > Forms. */
export const createCrmForm = (name: string) => api<CrmFormSummary>('/api/crm/forms', json('POST', { name }));

export const listCrmCalendars = () => api<CrmCalendarSummary[]>('/api/crm/calendars');

/** Where staff edit a CRM form. */
export const crmFormEditorPath = (formId: number) => `/crm/forms?form=${formId}`;

// ── Public links ─────────────────────────────────────────────

/** Path of a page on any host that serves pages. */
export const pagePath = (slug: string) => `/p/${slug}`;

/**
 * The address to share for a page, the same rule the server uses for its
 * canonical link: the live domain whose homepage it is, else the first live
 * domain (/<slug>), else the app's own /p/<slug>.
 */
export function bestPublicUrl(page: { id: string; slug: string }, domains: PageDomain[] | null | undefined, origin?: string) {
  const live = (domains ?? []).filter((d) => d.status === 'active');
  const home = live.find((d) => d.root_page_id === page.id);
  if (home) return `https://${home.domain}/`;
  if (live[0]) return `https://${live[0].domain}/${page.slug}`;
  return publicPageUrl(page.slug, { origin });
}

/** Full public link: on a custom domain the page lives at /<slug> (or / when it is that domain's homepage). */
export function publicPageUrl(slug: string, opts: { origin?: string; domain?: PageDomain | null } = {}) {
  const { domain } = opts;
  if (domain) return domain.root_page?.slug === slug ? `https://${domain.domain}/` : `https://${domain.domain}/${slug}`;
  const origin = opts.origin ?? (typeof window !== 'undefined' ? window.location.origin : '');
  return `${origin}${pagePath(slug)}`;
}
